import React, { useMemo, useState } from 'react';
import { api } from '../api';
import { computeSOWCosts } from '../computeSOWCosts';
import SOWCostTable from './SOWCostTable';

const SELECT = ({ value, onChange, options }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value)}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

function Field({ label, children }) {
  return (
    <div style={{marginBottom:10}}>
      <label style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',display:'block',marginBottom:3}}>{label}</label>
      {children}
    </div>
  );
}

function NumberInput({ value, onChange, placeholder }) {
  return (
    <input type="number" defaultValue={value ?? ''} placeholder={placeholder || ''}
      onBlur={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      style={{ width: '100%' }} />
  );
}

function CheckRow({ label, checked, onChange }) {
  return (
    <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,marginBottom:8}}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked ? 1 : 0)}
        style={{width:15,height:15,accentColor:'var(--brand)'}} />
      {label}
    </label>
  );
}

function SectionHeader({ label }) {
  return (
    <div style={{gridColumn:'span 2',marginTop:12,marginBottom:6,
      fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',
      color:'var(--brand)',borderBottom:'1px solid var(--border)',paddingBottom:4}}>
      {label}
    </div>
  );
}

const CONDITION = [['','— Select —'],['good','Good'],['fair','Fair'],['poor','Poor']];

export default function LandscapeSection({ deal, onUpdate, statusLabel, statusColor }) {
  const [data, setData] = useState(deal.sow_landscape || {});
  const [open, setOpen] = useState(false);

  const costLines = useMemo(() => {
    const all = computeSOWCosts({ ...deal, sow_landscape: data });
    return all.filter(l => ['landscape','fence','driveway'].includes(l.section));
  }, [deal, data]);

  const save = async (field, value) => {
    const updated = { ...data, [field]: value };
    setData(updated);
    await api.updateSowLandscape(deal.id, { [field]: value });
  };

  const summaryParts = [
    data.landscape_scope && data.landscape_scope !== 'cleanup' && `Landscape: ${data.landscape_scope.replace(/_/g,' ')}`,
    data.sod && 'Sod',
    data.tree_removal_count && `${data.tree_removal_count} tree removal`,
    data.fence_action && data.fence_action !== 'none' && `Fence: ${data.fence_action}`,
    data.driveway_action && data.driveway_action !== 'keep' && `Driveway: ${data.driveway_action}`,
  ].filter(Boolean);

  return (
    <div className="card" style={{marginBottom:16,padding:0,overflow:'hidden'}}>
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer',
        borderLeft:'4px solid #39b54a'}} onClick={() => setOpen(o => !o)}>
        <div style={{flex:1}}>
          <div style={{fontWeight:600,fontSize:14}}>Landscape & Hardscape</div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>
            {summaryParts.length ? summaryParts.join(' · ') : 'Landscape · Fence · Driveway'}
          </div>
        </div>
        {statusLabel && (
          <span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:12,
            background:`${statusColor}22`,color:statusColor,whiteSpace:'nowrap'}}>
            {statusLabel}
          </span>
        )}
        <span style={{color:'var(--text-muted)',fontSize:16}}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{padding:'16px',borderTop:'1px solid var(--bg)'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 20px'}}>

            {/* ── Landscape ── */}
            <SectionHeader label="Landscape" />
            <Field label="Landscape Scope">
              <SELECT value={data.landscape_scope} onChange={v => save('landscape_scope', v)}
                options={[['cleanup','Cleanup Only'],['light_refresh','Light Refresh'],
                  ['full_refresh','Full Refresh'],['new_install','New Install']]} />
            </Field>
            <Field label="Irrigation">
              <SELECT value={data.irrigation} onChange={v => save('irrigation', v)}
                options={[['none','None'],['repair','Repair Existing'],['new','New System']]} />
            </Field>
            <div style={{gridColumn:'span 2'}}>
              <CheckRow label="Sod" checked={data.sod} onChange={v => save('sod', v)} />
              {!!data.sod && (
                <div style={{paddingLeft:24,marginBottom:8}}>
                  <Field label="Sod Sq Ft">
                    <NumberInput value={data.sod_sq_ft} onChange={v => save('sod_sq_ft', v)} placeholder="2000" />
                  </Field>
                </div>
              )}
            </div>
            <Field label="Tree Removals">
              <NumberInput value={data.tree_removal_count} onChange={v => save('tree_removal_count', v)} placeholder="0" />
            </Field>

            {/* ── Fence ── */}
            <SectionHeader label="Fence" />
            <Field label="Fence Type">
              <SELECT value={data.fence_type} onChange={v => save('fence_type', v)}
                options={[['none','None / N/A'],['wood','Wood'],['chain_link','Chain Link'],
                  ['vinyl','Vinyl'],['iron','Iron / Aluminum'],['other','Other']]} />
            </Field>
            {data.fence_type && data.fence_type !== 'none' && (<>
              <Field label="Fence Condition">
                <SELECT value={data.fence_condition} onChange={v => save('fence_condition', v)} options={CONDITION} />
              </Field>
              <Field label="Fence Action">
                <SELECT value={data.fence_action} onChange={v => save('fence_action', v)}
                  options={[['none','None'],['repair','Repair'],['replace','Replace'],['new','New Install']]} />
              </Field>
              {data.fence_action && data.fence_action !== 'none' && (
                <Field label="Linear Ft">
                  <NumberInput value={data.fence_linear_ft} onChange={v => save('fence_linear_ft', v)} placeholder="150" />
                </Field>
              )}
            </>)}

            {/* ── Driveway ── */}
            <SectionHeader label="Driveway" />
            <Field label="Driveway Material">
              <SELECT value={data.driveway_material} onChange={v => save('driveway_material', v)}
                options={[['','— Select —'],['concrete','Concrete'],['asphalt','Asphalt'],
                  ['gravel','Gravel'],['pavers','Pavers'],['none','None / Dirt']]} />
            </Field>
            <Field label="Condition">
              <SELECT value={data.driveway_condition} onChange={v => save('driveway_condition', v)} options={CONDITION} />
            </Field>
            <Field label="Action">
              <SELECT value={data.driveway_action} onChange={v => save('driveway_action', v)}
                options={[['keep','Keep'],['repair','Repair / Seal'],['replace','Replace']]} />
            </Field>
            {data.driveway_action && data.driveway_action !== 'keep' && (
              <Field label="Driveway Sq Ft">
                <NumberInput value={data.driveway_sq_ft} onChange={v => save('driveway_sq_ft', v)} placeholder="600" />
              </Field>
            )}

            {/* ── Notes ── */}
            <div style={{gridColumn:'span 2',marginTop:8}}>
              <Field label="Notes">
                <textarea rows={2} defaultValue={data.notes || ''}
                  onBlur={e => save('notes', e.target.value)}
                  style={{resize:'vertical',width:'100%'}} placeholder="Retaining walls, pool, shed, mailbox…" />
              </Field>
            </div>

          </div>

          <SOWCostTable dealId={deal.id} lines={costLines} sowCosts={deal.sow_costs} onSynced={onUpdate} />
        </div>
      )}
    </div>
  );
}
